import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { DatePicker } from '@/components/ui/date-picker';
import { AlertTriangle, Lock } from 'lucide-react';
import type { FinancialYearDto, UpdateFinancialYearRequest } from '../../services/financialYearsApi';

interface EditFinancialYearDialogProps {
  open: boolean;
  fy: FinancialYearDto;
  /** True when the current user holds financial_years.approve_creation (GM level). */
  isGm: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (payload: UpdateFinancialYearRequest, gmOverride: boolean) => void;
  submitting: boolean;
}

const toDateInput = (iso: string) => iso.slice(0, 10); // "2026-01-01T00:00:00Z" → "2026-01-01"

export const EditFinancialYearDialog = ({
  open,
  fy,
  isGm,
  onOpenChange,
  onSubmit,
  submitting,
}: EditFinancialYearDialogProps) => {
  const [form, setForm] = useState({
    code: fy.code,
    name: fy.name,
    startDate: toDateInput(fy.startDate),
    endDate: toDateInput(fy.endDate),
  });

  // Reset form when the FY prop changes or dialog re-opens.
  useEffect(() => {
    setForm({
      code: fy.code,
      name: fy.name,
      startDate: toDateInput(fy.startDate),
      endDate: toDateInput(fy.endDate),
    });
  }, [fy, open]);

  const set = (field: keyof typeof form) => (value: string) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const isLocked = fy.hasTransactions && !isGm;
  const requiresGmOverride = fy.hasTransactions && isGm;

  const valid =
    !isLocked &&
    form.code.trim().length > 0 &&
    form.name.trim().length > 0 &&
    form.startDate.length > 0 &&
    form.endDate.length > 0 &&
    form.endDate > form.startDate;

  const handleSubmit = () => {
    if (!valid) return;
    onSubmit(
      {
        code: form.code.trim(),
        name: form.name.trim(),
        startDate: form.startDate,
        endDate: form.endDate,
      },
      requiresGmOverride,
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Edit Financial Year</DialogTitle>
          <DialogDescription>
            Update the code, name and date boundaries for <strong>{fy.name || fy.label}</strong>.
          </DialogDescription>
        </DialogHeader>

        {isLocked && (
          <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
            <Lock className="size-4 mt-0.5 shrink-0" />
            <span>
              This financial year has transactions posted to it. Only a General Manager can
              edit its details. Contact your GM to make changes.
            </span>
          </div>
        )}

        {requiresGmOverride && (
          <div className="flex items-start gap-2 rounded-md border border-orange-200 bg-orange-50 p-3 text-sm text-orange-800">
            <AlertTriangle className="size-4 mt-0.5 shrink-0" />
            <span>
              This financial year has transactions. As GM you can override the lock, but
              changing date boundaries may affect which transactions fall inside the year.
            </span>
          </div>
        )}

        <div className={`space-y-4 mt-2 ${isLocked ? 'opacity-50 pointer-events-none' : ''}`}>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 [&>*]:min-w-0">
            <div>
              <Label htmlFor="edit-fy-code">Code</Label>
              <Input
                id="edit-fy-code"
                placeholder="e.g. FY2026"
                value={form.code}
                onChange={(e) => set('code')(e.target.value)}
                disabled={submitting}
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="edit-fy-name">Name</Label>
              <Input
                id="edit-fy-name"
                placeholder="e.g. Financial Year 2026"
                value={form.name}
                onChange={(e) => set('name')(e.target.value)}
                disabled={submitting}
                className="mt-1"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 [&>*]:min-w-0">
            <div>
              <Label htmlFor="edit-fy-start">Start Date</Label>
              <DatePicker
                id="edit-fy-start"
                value={form.startDate}
                onChange={set('startDate')}
                disabled={submitting}
                max={form.endDate || undefined}
                className="mt-1 w-full"
              />
            </div>
            <div>
              <Label htmlFor="edit-fy-end">End Date</Label>
              <DatePicker
                id="edit-fy-end"
                value={form.endDate}
                onChange={set('endDate')}
                disabled={submitting}
                min={form.startDate || undefined}
                className="mt-1 w-full"
              />
            </div>
          </div>

          {form.endDate && form.startDate && form.endDate <= form.startDate && (
            <p className="text-xs text-red-600">End date must be after start date.</p>
          )}
        </div>

        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            Cancel
          </Button>
          {!isLocked && (
            <Button onClick={handleSubmit} disabled={!valid || submitting}>
              {submitting ? 'Saving…' : requiresGmOverride ? 'Save (GM Override)' : 'Save'}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
